import { useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataTable,
  type Column,
  MilestoneStrip,
  NarrativeCard,
  Pill,
  HealthPill,
  EntityStatusPill,
  Button,
  Stat,
  ProgressBar,
  StatusRibbon,
  type SpendRibbonState,
  RightRail,
  RailSection,
  AttentionList,
  ActivityList,
  EmptyState,
} from "@/design/components";
import { WorkspaceHeader } from "@/shell/WorkspaceHeader";
import { formatHumanCurrency } from "@/design/format";
import {
  useActiveWorkspace,
  useActiveProfile,
  useProgramme,
  useProjects,
  useMilestones,
  useSeed,
  useTerminology,
} from "@/domain/hooks";
import type { Project } from "@/domain/types";

const RIBBON_STATES: SpendRibbonState[] = [
  "Proposed",
  "Approved for spend",
  "Committed",
  "Evidence submitted",
  "Confirmed",
];

export function FoundationShowcase() {
  const ws = useActiveWorkspace();
  const profile = useActiveProfile();
  const programme = useProgramme();
  const projects = useProjects();
  const seed = useSeed();
  const term = useTerminology();
  const milestones = useMilestones("fs-04-entebbe");
  const [ribbon, setRibbon] = useState(2);
  const [railCollapsed, setRailCollapsed] = useState(false);

  const totals = useMemo(() => {
    const spent = projects.reduce((s, p) => s + p.spent, 0);
    const evidenced = projects.reduce((s, p) => s + p.evidenced, 0);
    const redundancy = projects.reduce((s, p) => s + p.redundancy, 0);
    const pending = seed.spendRequests.filter((r) => r.state === "proposed").length;
    return { spent, evidenced, redundancy, pending };
  }, [projects, seed.spendRequests]);

  const projectColumns: Column<Project>[] = [
    {
      key: "name",
      header: term.project,
      cell: (p) => (
        <div>
          <p className="font-medium text-ink">{p.name}</p>
          <p className="text-xs text-faint">{p.district} · {p.region}</p>
        </div>
      ),
    },
    {
      key: "budget",
      header: "Budgeted",
      align: "right",
      cell: (p) => <span className="tabular-nums text-mute">{formatHumanCurrency(p.budgeted)}</span>,
    },
    {
      key: "spent",
      header: "Spent",
      cell: (p) => (
        <div className="w-32">
          <span className="text-sm tabular-nums text-ink">{formatHumanCurrency(p.spent)}</span>
          <ProgressBar value={p.budgeted ? p.spent / p.budgeted : 0} className="mt-1" />
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (p) => <EntityStatusPill status={p.status} />,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <WorkspaceHeader />
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 overflow-y-auto px-8 py-6">
          <div className="mx-auto max-w-6xl space-y-8">
            {/* Title */}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight text-ink">Foundation</h1>
                <HealthPill rag={programme.alertState} />
              </div>
              <p className="mt-1 max-w-2xl text-sm text-mute">
                Design system, providers, switchers and mock-data spine are in place. Switch the
                workspace or profile in the top-right to see the accent repaint and the data rescope.
                Active identity: {profile.displayName}, {profile.role} at {profile.organisation}.
              </p>
            </div>

            {/* Stat hero strip */}
            <section className="grid grid-cols-2 gap-4 md:grid-cols-5">
              <Stat label="Total budgeted" value={formatHumanCurrency(programme.totalBudget)} sub="across 35 fire stations" />
              <Stat
                label="Spent"
                value={formatHumanCurrency(totals.spent)}
                trend={<ProgressBar value={totals.spent / programme.totalBudget} />}
              />
              <Stat
                label="Evidenced and confirmed"
                value={formatHumanCurrency(totals.evidenced)}
                trend={<ProgressBar value={totals.evidenced / Math.max(1, totals.spent)} tone="ok" />}
              />
              <Stat label="Contingency held" value={formatHumanCurrency(totals.redundancy)} sub="25% of budget" />
              <Stat
                label="Pending approvals"
                value={`${totals.pending} spend asks`}
                sub="2 overdue 8h+"
                subTone="warn"
              />
            </section>

            {/* Milestone strip */}
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-faint">
                Milestones · Fire Station #04 · Entebbe
              </h2>
              <MilestoneStrip milestones={milestones} variant="full" />
            </section>

            {/* Two-column content */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>{term.project}s</CardTitle>
                    <Pill variant="outline">{projects.length} total</Pill>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <DataTable
                      columns={projectColumns}
                      rows={projects}
                      rowKey={(p) => p.id}
                      empty={
                        <EmptyState
                          title="No projects yet"
                          body="Projects appear here once a procurement plan is committed for this workspace."
                        />
                      }
                    />
                  </CardBody>
                </Card>

                {/* Spend status ribbon demo */}
                <Card>
                  <CardHeader>
                    <CardTitle>Spend status ribbon</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setRibbon((r) => Math.max(0, r - 1))}>
                        Back
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setRibbon((r) => Math.min(4, r + 1))}>
                        Advance
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody className="pt-4">
                    <StatusRibbon current={RIBBON_STATES[ribbon]} />
                  </CardBody>
                </Card>

                {/* Component reference */}
                <Card>
                  <CardHeader>
                    <CardTitle>Component reference</CardTitle>
                  </CardHeader>
                  <CardBody className="space-y-4 pt-4">
                    <div className="flex flex-wrap gap-2">
                      <Pill variant="success" dot>Green</Pill>
                      <Pill variant="warn" dot>Amber</Pill>
                      <Pill variant="danger" dot>Red</Pill>
                      <Pill variant="info">Info</Pill>
                      <Pill variant="brand">Brand</Pill>
                      <Pill variant="neutral">Neutral</Pill>
                      <Pill variant="outline">Outline</Pill>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button>Primary</Button>
                      <Button variant="secondary">Secondary</Button>
                      <Button variant="ghost">Ghost</Button>
                      <Button variant="danger">Danger</Button>
                      <Button variant="link">Link</Button>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <div className="space-y-6">
                <NarrativeCard
                  variant="briefing"
                  title="This week"
                  body={seed.briefing.whereWeAre}
                  figures={[
                    { label: "Budget consumed", value: "47%" },
                    { label: "Time elapsed", value: "42%" },
                    { label: "Pace", value: "+5.3%", tone: "warn" },
                  ]}
                  cta={{ label: "Open analytics", href: "/stratus/insights/pace" }}
                />
                <NarrativeCard
                  variant="flag"
                  title="Watchlist"
                  body={seed.briefing.watchlist}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Right rail */}
        <RightRail collapsed={railCollapsed} onToggle={() => setRailCollapsed((c) => !c)}>
          <RailSection title="Today's attention list">
            <AttentionList items={seed.attentionList} />
          </RailSection>
          <RailSection title="Today's briefing">
            <NarrativeCard variant="briefing" title="What is coming next" body={seed.briefing.whatIsNext} />
          </RailSection>
          <RailSection title="Recent activity">
            <ActivityList
              items={seed.auditEvents.slice(0, 5).map((e) => ({
                id: e.id,
                text: e.description,
                time: e.timestamp,
              }))}
            />
          </RailSection>
        </RightRail>
      </div>
    </div>
  );
}
